import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { LockClosedIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import authService from '../../services/auth.service';
import { getAuthErrorMessage } from '../../utils/authErrorHandler';
import AuthShell from './AuthShell';
import AuthField from './AuthField';
import AuthSubmitButton from './AuthSubmitButton';

const PasswordResetConfirm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!token) navigate('/reset-password');
  }, [token, navigate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (password.length < 8) {
      newErrors.password = t('auth:passwordResetConfirm.passwordTooShort');
    }
    if (!/[A-Z]/.test(password)) {
      newErrors.password = t('auth:passwordResetConfirm.passwordMissingUppercase');
    }
    if (!/[a-z]/.test(password)) {
      newErrors.password = t('auth:passwordResetConfirm.passwordMissingLowercase');
    }
    if (!/\d/.test(password)) {
      newErrors.password = t('auth:passwordResetConfirm.passwordMissingDigit');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      newErrors.password = t('auth:passwordResetConfirm.passwordMissingSpecial');
    }
    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth:passwordResetConfirm.passwordsDoNotMatch');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: 'password' | 'confirmPassword',
    value: string
  ) => {
    if (field === 'password') {
      setPassword(value);
    } else {
      setConfirmPassword(value);
    }
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const getPasswordStrength = () => {
    if (!password) return { score: 0, text: '', barClass: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    const tiers = [
      { text: t('auth:passwordResetConfirm.veryWeak'), barClass: 'bg-red-500' },
      { text: t('auth:passwordResetConfirm.weak'), barClass: 'bg-orange-500' },
      { text: t('auth:passwordResetConfirm.fair'), barClass: 'bg-amber-500' },
      { text: t('auth:passwordResetConfirm.good'), barClass: 'bg-sky-500' },
      { text: t('auth:passwordResetConfirm.strong'), barClass: 'bg-emerald-500' },
    ];
    return { score, ...tiers[Math.min(score, 4)] };
  };

  const passwordStrength = getPasswordStrength();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    if (!validateForm()) return;
    if (!token) {
      setErrors({ general: t('auth:passwordResetConfirm.invalidResetLink') });
      return;
    }
    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      await authService.confirmResetPassword({ token, newPassword: password });
      setIsSubmitted(true);
    } catch (error: any) {
      const errorMessage = getAuthErrorMessage(error, t, 'passwordResetConfirm');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // ---------- Success state ----------
  if (isSubmitted) {
    return (
      <AuthShell title={t('auth:passwordResetConfirm.successTitle')}>
        <div className="flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/30">
            <CheckCircleIcon className="h-6 w-6 text-emerald-400" />
          </div>
          <p className="mt-6 text-base text-white/70">
            {t('auth:passwordResetConfirm.successMessage')}
          </p>
          <div className="mt-10 w-full">
            <Link
              to="/signin"
              className="w-full inline-flex items-center justify-center px-5 py-3 text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {t('auth:passwordResetConfirm.goToSignIn')}
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  // ---------- Form state ----------
  return (
    <AuthShell
      title={t('auth:passwordResetConfirm.title')}
      subtitle={t('auth:passwordResetConfirm.subtitle')}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <AuthField
            id="password"
            name="password"
            type="password"
            label={t('auth:passwordResetConfirm.passwordLabel')}
            value={password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder={t('auth:passwordResetConfirm.passwordPlaceholder')}
            error={errors.password}
            icon={LockClosedIcon}
            required
            autoComplete="new-password"
            helpText={t('auth:passwordResetConfirm.passwordRequirements')}
          />
          {password && (
            <div className="mt-3">
              <div className="flex justify-between items-center text-xs mb-1.5">
                <span className="text-white/60">
                  {t('auth:passwordResetConfirm.passwordStrength')}
                </span>
                <span className="text-white/80">{passwordStrength.text}</span>
              </div>
              <div className="w-full bg-white/[0.05] h-1.5 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${passwordStrength.barClass}`}
                  style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <AuthField
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          label={t('auth:passwordResetConfirm.confirmPasswordLabel')}
          value={confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
          placeholder={t('auth:passwordResetConfirm.passwordPlaceholder')}
          error={errors.confirmPassword}
          icon={LockClosedIcon}
          required
          autoComplete="new-password"
        />

        <AuthSubmitButton
          isLoading={isLoading}
          loadingLabel={t('auth:passwordResetConfirm.submittingButton')}
        >
          {t('auth:passwordResetConfirm.submitButton')}
        </AuthSubmitButton>
      </form>

      <p className="mt-8 text-center text-sm">
        <Link
          to="/signin"
          className="text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black font-medium"
        >
          {t('auth:passwordResetConfirm.backToSignIn')} →
        </Link>
      </p>
    </AuthShell>
  );
};

export default PasswordResetConfirm;
